import React, { useState } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
} from "@mui/material";
import axios from "axios";

const EmailValidationDashboard = () => {
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const validateEmailFormat = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const detectEmailProvider = (mxRecord) => {
    if (mxRecord.includes("google")) return "Google Apps (Gmail)";
    if (mxRecord.includes("outlook.com")) return "Outlook (Microsoft 365)";
    return mxRecord;
  };

  const checkMXRecords = async (domain) => {
    try {
      const response = await fetch(`https://dns.google/resolve?name=${domain}&type=MX`);
      const data = await response.json();
      if (data.Answer && data.Answer.length > 0) {
        const mxRecord = data.Answer[0].data.split(" ").pop();
        return { hasMX: true, provider: detectEmailProvider(mxRecord) };
      }
      return { hasMX: false, provider: "Unknown" };
    } catch (error) {
      return { hasMX: false, provider: "Unknown" };
    }
  };

  const getRowStyle = (status) => {
    switch (status) {
      case "Valid":
        return { backgroundColor: "#d4edda" }; // Light green
      case "To Be Checked":
        return { backgroundColor: "#fff3cd" }; // Light yellow
      case "Invalid":
        return { backgroundColor: "#f8d7da" }; // Light red
      default:
        return {};
    }
  };

  const handleValidateEmails = async () => {
    const emailList = emailInput.split(/\s|,|;/).filter((email) => email.trim());
    setEmails(emailList);
    setLoading(true);

    const validationResults = await Promise.all(
      emailList.map(async (email) => {
        if (!validateEmailFormat(email)) {
          return { email, status: "Invalid", reason: "Invalid email format", provider: "N/A" };
        }

        const domain = email.split("@")[1];
        const { hasMX, provider } = await checkMXRecords(domain);

        if (!hasMX) {
          return { email, status: "Invalid", reason: "No MX records found", provider };
        }

        const backendResponse = await axios.post("/api/validate-emails", { emails: [email] });
        return { ...backendResponse.data.data[0], provider };
      })
    );

    setResults(validationResults);
    setLoading(false);
  };

  const handleSendTestEmails = async () => {
    try {
      await axios.post("/api/send-test-email", { emails: selectedEmails });
      alert(`Test emails sent to selected addresses`);
    } catch (error) {
      alert(`Failed to send test emails`);
    }
  };

  const handleSelectEmail = (email) => {
    setSelectedEmails((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedEmails([]);
    } else {
      setSelectedEmails(results.filter((res) => res.status === "To Be Checked").map((res) => res.email));
    }
    setSelectAll(!selectAll);
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Email Validation Dashboard
      </Typography>
      <TextField
        label="Enter Emails (comma/space-separated)"
        multiline
        rows={4}
        fullWidth
        variant="outlined"
        value={emailInput}
        onChange={(e) => setEmailInput(e.target.value)}
      />
      <Button
        variant="contained"
        color="primary"
        onClick={handleValidateEmails}
        disabled={loading}
        style={{ marginTop: 16 }}
      >
        {loading ? <CircularProgress size={24} /> : "Validate Emails"}
      </Button>

      {results.length > 0 && (
        <TableContainer component={Paper} style={{ marginTop: 20 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Checkbox checked={selectAll} onChange={handleSelectAll} />
                </TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((result, index) => (
                <TableRow key={index} style={getRowStyle(result.status)}>
                  <TableCell>
                    {result.status === "To Be Checked" && (
                      <Checkbox
                        checked={selectedEmails.includes(result.email)}
                        onChange={() => handleSelectEmail(result.email)}
                      />
                    )}
                  </TableCell>
                  <TableCell>{result.email}</TableCell>
                  <TableCell>{result.status}</TableCell>
                  <TableCell>{result.reason}</TableCell>
                  <TableCell>{result.provider}</TableCell>
                  <TableCell>
                    {result.status === "To Be Checked" && (
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() => handleSendTestEmails()}
                      >
                        Send All Test Emails
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default EmailValidationDashboard;
